import styled from "@emotion/styled";
import { Box, Button, CompassColor, Text } from "@noom/wax-component-library";
import React from "react";

import locationIcon from "../../image_assets/location.svg";

export type DisplayableShippingAddress = {
  recipient?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  zoneCode?: string | null;
  postalCode?: string | null;
};

const LocationIcon = styled.img`
  width: 30px;
  height: 30px;
`;

const UpdateButton = styled(Button)`
  border-radius: 9999px;
`;

type ShippingAddressSectionProps = {
  addressInfo: DisplayableShippingAddress | null | undefined;
  onUpdate: () => void;
  emptyMessage?: string;
};

export const ShippingAddressSection = ({
  addressInfo,
  onUpdate,
  emptyMessage = "No shipping address on file",
}: ShippingAddressSectionProps) => {
  const hasAddress =
    !!addressInfo?.addressLine1 ||
    !!addressInfo?.city ||
    !!addressInfo?.zoneCode ||
    !!addressInfo?.postalCode;

  return (
    <Box
      marginBottom="16px"
      width="100%"
      padding="24px"
      backgroundColor={CompassColor.white}
      borderRadius="8px"
      fontFamily="Untitled Sans, sans-serif"
      data-cy="shipping-address-section"
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="20px"
      >
        <Text fontSize="18px" fontWeight="500" margin="0" data-cy="shipping-address-title">
          Shipping address
        </Text>
        <UpdateButton
          size="sm"
          color={CompassColor.black}
          colorScheme="secondary"
          className="button"
          type="button"
          variant="outline"
          onClick={onUpdate}
          data-cy="shipping-address-update-button"
        >
          Update
        </UpdateButton>
      </Box>

      <Box display="flex" alignItems="flex-start">
        <Box
          width="32px"
          height="32px"
          marginRight="16px"
          display="flex"
          alignItems="flex-start"
          justifyContent="center"
          paddingTop="2px"
        >
          <LocationIcon src={locationIcon} alt="Location" data-cy="location-icon" />
        </Box>
        <Box data-cy="address-details">
          {!hasAddress && (
            <Text fontSize="15px" color={CompassColor.black} data-cy="address-empty">
              {emptyMessage}
            </Text>
          )}
          {hasAddress && addressInfo?.recipient && (
            <Text
              fontSize="16px"
              fontWeight="500"
              color={CompassColor.black}
              marginBottom="4px"
              data-cy="address-recipient"
            >
              {addressInfo.recipient}
            </Text>
          )}
          {addressInfo?.addressLine1 && (
            <Text
              fontSize="15px"
              color={CompassColor.black}
              lineHeight="1.4"
              display="block"
              marginBottom="0"
              data-cy="address-line1"
            >
              {addressInfo.addressLine1}
            </Text>
          )}
          {addressInfo?.addressLine2 && (
            <Text
              fontSize="15px"
              color={CompassColor.black}
              lineHeight="1.4"
              display="block"
              marginBottom="0"
              data-cy="address-line2"
            >
              {addressInfo.addressLine2}
            </Text>
          )}
          {(addressInfo?.city || addressInfo?.zoneCode || addressInfo?.postalCode) && (
            <Text
              fontSize="15px"
              color={CompassColor.black}
              lineHeight="1.4"
              display="block"
              marginBottom="0"
              data-cy="address-city-state-postal"
            >
              {`${addressInfo?.city || ""}${addressInfo?.city ? ", " : ""}${
                addressInfo?.zoneCode || ""
              } ${addressInfo?.postalCode?.slice(0, 5) || ""}`.trim()}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
};
